import React, { useState, useEffect } from "react";

export default function AhrefsBatchFetcher() {
  const [input, setInput] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [copied, setCopied] = useState(false);
  const [arranged, setArranged] = useState(false);
  const [domainsLeft, setDomainsLeft] = useState(null);

  const FUNCTION_URL = "https://getahrefsdata-3mwuzz3buq-uc.a.run.app";
  const CONCURRENCY = 10;
  const SUBSCRIPTION_URL =
    "https://us-central1-ahrefs-edu-gov.cloudfunctions.net/getAhrefsSubscriptionInfo";

  // 🔐 Simple Authentication
  const [authorized, setAuthorized] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");

  const correctPassword = "Slava123"; // change this

  const handleLogin = () => {
    if (passwordInput === correctPassword) {
      setAuthorized(true);
    } else {
      alert("Wrong password");
    }
  };

  // Fetch subscription info on load
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await fetch(SUBSCRIPTION_URL);
        const data = await res.json();
        const rowsLeft = data?.info?.rows_left ?? 0;
        setDomainsLeft(Math.floor(rowsLeft / 20));
      } catch (e) {
        console.error("Failed to fetch subscription info", e);
        setDomainsLeft(null);
      }
    };

    fetchSubscription();
  }, []);

  const handleFetch = async () => {
    const domains = input
      .split("\n")
      .map((d) => d.trim())
      .filter(Boolean);

    if (domains.length === 0) return;

    setOriginalOrder(domains);
    setLoading(true);
    setResults([]);
    setDone(false);
    setCopied(false);
    setArranged(false);

    let index = 0;

    const runBatch = async () => {
      while (index < domains.length) {
        const batch = domains.slice(index, index + CONCURRENCY);
        index += CONCURRENCY;

        await Promise.all(
          batch.map(async (domain) => {
            try {
              const response = await fetch(
                `${FUNCTION_URL}?target=${encodeURIComponent(domain)}`
              );
              const data = await response.json();
              const edu = data?.edu ?? 0;
              const gov = data?.gov ?? 0;
              setResults((prev) => [...prev, { domain, edu, gov }]);
            } catch {
              setResults((prev) => [...prev, { domain, edu: "-", gov: "-" }]);
            }
          })
        );
      }
    };

    await runBatch();
    setLoading(false);
    setDone(true);
  };

  const handleArrange = () => {
    setResults((prev) =>
      [...prev].sort(
        (a, b) =>
          originalOrder.indexOf(a.domain) - originalOrder.indexOf(b.domain)
      )
    );
    setArranged(true);
    setTimeout(() => setArranged(false), 2000);
  };

  const handleCopy = async () => {
    const lines = results.map((r) => `${r.domain}\t${r.edu}\t${r.gov}`);
    const text = lines.join("\n");

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert("Failed to copy results. Please try again.");
    }
  };

  // --------------------
  // 🔐 LOGIN SCREEN
  // --------------------
  if (!authorized) {
    return (
      <div style={loginStyles.container}>
        <div style={loginStyles.card}>
          <h2 style={loginStyles.title}>Login</h2>
          <input
            type="password"
            placeholder="Enter password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            style={loginStyles.input}
          />
          <button onClick={handleLogin} style={loginStyles.button}>
            Enter
          </button>
        </div>
      </div>
    );
  }

  // --------------------
  // MAIN APP
  // --------------------
  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>Ahrefs Batch EDU/GOV Counter</h1>

      {domainsLeft !== null && (
        <div style={styles.domainsLeft}>
          Domains left to check:{" "}
          <span style={{ fontWeight: 600 }}>{domainsLeft}</span>
        </div>
      )}

      <textarea
        style={styles.textarea}
        placeholder="Enter domains, one per line..."
        value={input}
        onChange={(e) => setInput(e.target.value)}
      />

      <div style={styles.buttonRow}>
        <button
          onClick={handleFetch}
          disabled={loading}
          style={{
            ...styles.button,
            backgroundColor: loading ? "#ccc" : "#2563eb",
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Fetching..." : "Fetch Data"}
        </button>

        {results.length > 0 && (
          <>
            <button
              onClick={handleArrange}
              style={{
                ...styles.button,
                backgroundColor: arranged ? "#059669" : "#10b981",
              }}
            >
              {arranged ? "Arranged ☑️" : "Arrange Domains"}
            </button>

            <button
              onClick={handleCopy}
              style={{
                ...styles.button,
                backgroundColor: copied ? "#d97706" : "#f59e0b",
              }}
            >
              {copied ? "Copied ☑️" : "Copy Results"}
            </button>
          </>
        )}
      </div>

      {results.length > 0 && (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={{ backgroundColor: "#f1f5f9" }}>
                <th style={styles.th}>Domain</th>
                <th style={styles.th}>EDUs</th>
                <th style={styles.th}>GOVs</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.domain} style={{ backgroundColor: "#fff" }}>
                  <td style={styles.td}>{r.domain}</td>
                  <td style={{ ...styles.td, textAlign: "center" }}>{r.edu}</td>
                  <td style={{ ...styles.td, textAlign: "center" }}>{r.gov}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {done && <div style={styles.done}>✅ Done!</div>}
        </div>
      )}
    </div>
  );
}

// --------------------
// LOGIN STYLES
// --------------------
const loginStyles = {
  container: {
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f3f4f6",
  },
  card: {
    padding: "30px",
    background: "#fff",
    borderRadius: "10px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: "300px",
    textAlign: "center",
  },
  title: {
    marginBottom: "20px",
    fontSize: "22px",
    fontWeight: "600",
  },
  input: {
    width: "100%",
    padding: "10px",
    marginBottom: "15px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "15px",
  },
  button: {
    width: "100%",
    padding: "10px",
    background: "#2563eb",
    color: "#fff",
    fontSize: "15px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
};

// --------------------
// MAIN APP STYLES
// --------------------
const styles = {
  container: {
    maxWidth: "700px",
    margin: "50px auto",
    padding: "24px",
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    fontFamily: "system-ui, sans-serif",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "4px",
    color: "#111827",
  },
  domainsLeft: {
    fontSize: "16px",
    color: "#374151",
    marginBottom: "16px",
  },
  textarea: {
    width: "100%",
    height: "150px",
    fontFamily: "monospace",
    borderRadius: "8px",
    border: "1px solid #ccc",
    padding: "10px",
    marginBottom: "12px",
  },
  buttonRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginBottom: "10px",
  },
  button: {
    padding: "10px 16px",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontSize: "15px",
    fontWeight: "500",
    transition: "all 0.2s ease-in-out",
  },
  tableWrapper: {
    marginTop: "20px",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    border: "1px solid #ccc",
    padding: "8px",
    textAlign: "left",
  },
  td: {
    border: "1px solid #ddd",
    padding: "8px",
  },
  done: {
    marginTop: "16px",
    textAlign: "center",
    color: "green",
    fontWeight: "600",
  },
};
